(() => {
  'use strict';
  const id = value => String(value ?? '').split('/').pop();
  const registryCache = new Map();
  const optionsOf = variant => Array.isArray(variant?.options) ? variant.options : [];
  const WATCH_KEY = 'lbdb-nonapple-watch:v1';
  const WATCH_EVENT = 'lbdb:nonapple-watch-change';
  const MAX_REGISTRY_ATTEMPTS = 3;
  const MAX_PICKER_ATTEMPTS = 50;
  const PICKER_RETRY_MS = 100;
  let watchInMemory = null, storageWriteFailed = false;

  const safe = callback => { try { return callback(); } catch (_) { return undefined; } };
  const attribute = (element, name) => safe(() => element?.getAttribute?.(name)) || '';
  const sameId = (first, second) => first !== undefined && first !== null && second !== undefined && second !== null && id(first) === id(second);
  const validProfile = value => value?.version === 1 && typeof value.brand === 'string' && value.brand.length < 80 && /^watch-\d+$/.test(value.modelId || '');

  function recallWatch() {
    if (storageWriteFailed) return watchInMemory;
    let raw;
    try { raw = window.sessionStorage.getItem(WATCH_KEY); } catch (_) { return watchInMemory; }
    try { const value = raw ? JSON.parse(raw) : null; return (watchInMemory = validProfile(value) ? value : null); }
    catch (_) { return (watchInMemory = null); }
  }
  function rememberWatch(watch) {
    watchInMemory = watch ? { version: 1, brand: watch.brand, modelId: watch.id } : null;
    try { if (watchInMemory) window.sessionStorage.setItem(WATCH_KEY, JSON.stringify(watchInMemory)); else window.sessionStorage.removeItem(WATCH_KEY); storageWriteFailed = false; }
    catch (_) { storageWriteFailed = true; }
    try {
      const active = JSON.parse(window.sessionStorage.getItem('lbdb-watch-finder:v1') || 'null');
      if (watchInMemory) window.sessionStorage.setItem('lbdb-watch-finder:v1', JSON.stringify(watchInMemory));
      else if (active?.brand && active.brand !== 'Apple') window.sessionStorage.removeItem('lbdb-watch-finder:v1');
    } catch (_) {}
  }
  function authorisedVariants(data, group) {
    if (!group || !Array.isArray(data?.variants)) return [];
    const ids = new Set((group.ids || []).map(id));
    return data.variants.filter(variant => {
      if (!ids.has(id(variant.id)) || optionsOf(variant).length !== data.options.length) return false;
      if (!group.position) return true;
      const value = optionsOf(variant)[group.position - 1] || '';
      if (!/mm\b/i.test(value) && (group.values || []).includes(value)) return true;
      if (group.width) return new RegExp('^\\s*' + group.width + '\\s*(?:mm|millimetri|millimeters?|millimetres?|millimètres?|millimeter|milímetros?|milimetri)\\s*$', 'i').test(value);
      // Les systèmes propriétaires ne sont jamais assimilés aux largeurs standard.
      if (group.system === 'samsung_ultra') return /ultra/i.test(value);
      if (group.system === 'samsung_watch8') return /(?:^|\D)8(?:\D|$)/.test(value);
      return false;
    });
  }
  function resolve(data, watches, watchId, currentOptions) {
    const watch = watches.find(w => w.id === watchId);
    if (!watch) return { status: watchId ? 'verify' : 'incomplete' };
    // Un modèle nommé sans preuve de son raccord reste à vérifier, jamais « incompatible » par défaut.
    if (watch.verification === 'unconfirmed' || watch.system === 'unknown' || (watch.width == null && watch.system === 'spring_bar_quick_release')) return { status: 'verify', watch };
    const matching = (data.groups || []).filter(group => group.watches.includes(watchId) && group.system === watch.system && group.width === watch.width);
    if (!matching.length) return { status: 'incompatible', watch };
    const candidates = matching.flatMap(group => authorisedVariants(data, group).map(variant => ({ variant, group })));
    if (!candidates.length) return { status: 'verify', watch };
    const current = Array.isArray(currentOptions) ? currentOptions : [];
    if (current.length !== data.options.length) return { status: 'incomplete', watch };
    const exact = candidates.filter(({ variant, group }) => optionsOf(variant).every((value, index) => index === group.position - 1 || value === current[index]));
    const available = exact.find(({ variant }) => variant.available === true);
    if (available) return { status: 'compatible', watch, group: available.group, variantId: available.variant.id, value: available.group.position ? optionsOf(available.variant)[available.group.position - 1] : '' };
    if (exact.length || !candidates.some(({ variant }) => variant.available === true)) return { status: 'soldout', watch, group: candidates[0].group };
    return { status: 'combination', watch, group: candidates[0].group };
  }
  function matchedWatch(data, watches, watchId, selectedVariant) {
    if (!selectedVariant?.available) return null;
    const answer = resolve(data, watches, watchId, optionsOf(selectedVariant));
    return answer.status === 'compatible' && id(answer.variantId) === id(selectedVariant.id) ? answer : null;
  }
  async function loadRegistry(url) {
    if (!registryCache.has(url)) {
      const promise = fetch(url, { credentials: 'same-origin' }).then(response => {
        if (!response.ok) throw new Error('Registry unavailable');
        return response.json();
      }).then(data => {
        if (!Array.isArray(data.watches) || !data.watches.length) throw new Error('Invalid registry');
        return data.watches;
      }).catch(error => { registryCache.delete(url); throw error; });
      registryCache.set(url, promise);
    }
    return registryCache.get(url);
  }

  class LBDBNonAppleWatchGuide extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._uiAbort = new AbortController();
      try {
        this._data = JSON.parse(this.querySelector('[data-na-data]').textContent);
        this._i18n = JSON.parse(this.querySelector('[data-na-i18n]').textContent);
      } catch (_) { return; }
      if (!Array.isArray(this._data.groups) || !this._data.groups.length) return;
      this._brand = this.querySelector('[data-na-brand]');
      this._model = this.querySelector('[data-na-model]');
      this._result = this.querySelector('[data-na-result]');
      this._body = this.querySelector('[data-na-body]');
      this._value = this.querySelector('[data-na-value]');
      this._apply = this.querySelector('[data-na-apply]');
      this._title = this.querySelector('[data-na-result-title]');
      this._connector = this.querySelector('[data-na-connector]');
      this._search = this.querySelector('[data-na-search]');
      this._suggestions = this.querySelector('[data-na-suggestions]');
      this._brandButtons = safe(() => Array.from(this.querySelectorAll?.('[data-na-brand-choice]'))) || [];
      this._activeIndex = -1;
      this._observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this._observer.disconnect(); this._initialise(); }
      }) : null;
      this._listen(document, WATCH_EVENT, event => {
        if (event.detail?.source === this) return;
        if (this._watches) this._restoreWatch();
      });
      this._listen(window, 'pageshow', () => this._reopen('pageshow'));
      this._listen(document, 'dialog:after-show', event => { if (event.target === this._drawer()) this._reopen('open'); });
      // Les boutons qui ouvrent le tiroir central portent le formulaire qui les a appelés.
      this._listen(document, 'click', event => this._rememberOpeningForm(event), { capture: true });
      if (this._watches) {
        this._activateInteractive(); this._restoreWatch(true); this._connectPicker();
      } else if (recallWatch()) this._initialise();
      else if (this._observer) this._observer.observe(this);
      else this._initialise();
    }

    disconnectedCallback() {
      this._connected = false;
      this._abort?.abort();
      this._uiAbort?.abort();
      this._observer?.disconnect();
      this._registryGeneration = (this._registryGeneration || 0) + 1;
      this._initialising = null;
      this._controlsBound = false;
      this._resetPicker();
    }

    _listen(element, name, callback, options = {}) {
      if (!element?.addEventListener || !this._abort?.signal) return;
      element.addEventListener(name, callback, { ...options, signal: this._abort.signal });
    }
    _listenUi(element, name, callback) {
      if (!element?.addEventListener || !this._uiAbort?.signal) return;
      element.addEventListener(name, callback, { signal: this._uiAbort.signal });
    }
    _text(key) { return this._i18n?.[key] || ''; }
    _guideHandle() { return this.dataset?.handle || attribute(this, 'data-handle'); }
    _registryUrl() { return this.dataset?.registry || attribute(this, 'data-registry'); }
    _drawer() { return safe(() => this.closest?.('x-drawer')) || safe(() => this.closest?.('[data-na-context="drawer"]')) || null; }
    _drawerId() { const drawer = this._drawer(); return drawer?.id || attribute(drawer, 'id'); }

    _reopen() {
      if (!this._connected) return;
      if (this._watches) {
        // Le picker peut avoir été hydraté après la première fenêtre d’essais.
        this._connectPicker(true);
        // Pendant cette reconnexion, _resolution reste en état loading : aucun résultat stale.
        this._restoreWatch(true);
      } else {
        // Aucune relance automatique : seules les ouvertures/retours utilisent le budget borné.
        this._initialise();
      }
    }

    _rememberOpeningForm(event) {
      const trigger = safe(() => event?.target?.closest?.('[data-lbdb-guide-form]'));
      const drawer = this._drawer();
      const drawerId = this._drawerId();
      if (!trigger || !drawer || !drawerId || attribute(trigger, 'aria-controls') !== drawerId) return;
      const opener = safe(() => trigger.closest?.('variant-picker'));
      if (!opener || !this._matchesGuideProduct(opener)) return;
      const formId = trigger.dataset?.lbdbGuideForm || attribute(trigger, 'data-lbdb-guide-form');
      const pickerForm = attribute(opener, 'form');
      if (!formId || (pickerForm && pickerForm !== formId)) return;
      if (!drawer.dataset) return;
      drawer.dataset.lbdbGuideForm = formId;
      this._reopen('open');
    }

    _associatedFormId() {
      // Les guides locaux restent propriétaires de leur formulaire, même si un tiroir global a été ouvert ailleurs.
      const local = this.dataset?.naForm || attribute(this, 'data-na-form') || attribute(this, 'form');
      if (local) return local;
      return this._drawer()?.dataset?.lbdbGuideForm || '';
    }

    _matchesGuideProduct(picker) {
      const expectedHandle = this._guideHandle();
      const expectedProductId = this._data?.productId;
      const pickerHandle = attribute(picker, 'handle');
      const product = safe(() => picker?.product);
      const pickerProductId = safe(() => product?.id);
      const handleMatches = !!expectedHandle && pickerHandle === expectedHandle;
      const productMatches = expectedProductId !== undefined && expectedProductId !== null && pickerProductId !== undefined && pickerProductId !== null && sameId(expectedProductId, pickerProductId);
      // Si les deux identifiants sont lisibles, ils ne doivent jamais se contredire.
      if (handleMatches && expectedProductId !== undefined && expectedProductId !== null && pickerProductId !== undefined && pickerProductId !== null && !productMatches) return false;
      return handleMatches || productMatches;
    }

    _formIdOf(picker, state) {
      return attribute(picker, 'form') || attribute(state?.form, 'id') || (typeof state?.form?.id === 'string' ? state.form.id : '');
    }

    _findPicker() {
      // Le handle est le cas normal, mais l'identité produit reste un raccord valide et
      // sûr pour une instance dont le composant n'a pas encore reflété cet attribut.
      const all = safe(() => Array.from(document.querySelectorAll('variant-picker'))) || [];
      const matches = all.filter(picker => this._matchesGuideProduct(picker));
      const formId = this._associatedFormId();
      if (formId) return matches.find(picker => this._formIdOf(picker) === formId) || null;
      // Le repli global n’est sûr que s’il n’existe qu’un seul picker correspondant.
      return matches.length === 1 ? matches[0] : null;
    }

    _pickerState(picker) {
      try {
        const product = picker?.product;
        const variants = product?.variants;
        const selected = picker?.selectedVariant;
        const master = picker?.masterSelector;
        let form = master?.form || null;
        if (!form) {
          const formId = attribute(picker, 'form');
          form = formId && typeof document !== 'undefined' ? document.forms?.[formId] || null : null;
        }
        if (!product || !Array.isArray(variants) || !selected || !Array.isArray(selected.options)) return null;
        return { product, variants, selected, master, form, formId: this._formIdOf(picker, { form }) };
      } catch (_) { return null; }
    }

    _pickerUsable(picker, requireForm = true) {
      const state = this._pickerState(picker);
      if (!state || (requireForm && !state.form)) return null;
      const formId = this._associatedFormId();
      if (formId && state.formId !== formId) return null;
      return state;
    }

    _clearPickerMatch(picker) {
      const slots = safe(() => Array.from(picker?.querySelectorAll?.('[data-na-watch-match]') || [])) || [];
      for (const slot of slots) { slot.hidden = true; slot.textContent = ''; delete slot.dataset.watchId; }
    }

    _resetPicker() {
      clearTimeout(this._pickerTimer);
      this._pickerTimer = null;
      this._pickerGeneration = (this._pickerGeneration || 0) + 1;
      this._pickerConnecting = false;
      this._pickerAbort?.abort();
      this._pickerAbort = null;
      this._clearPickerMatch(this._picker);
      this._picker = null;
      this._pickerForm = null;
    }

    _connectPicker(force = false) {
      if (!this._connected || typeof window === 'undefined' || !window.customElements?.whenDefined) return;
      if (force) this._resetPicker();
      else if (this._pickerUsable(this._picker) || this._pickerConnecting) return;
      const generation = (this._pickerGeneration || 0) + 1;
      this._pickerGeneration = generation;
      this._pickerConnecting = true;
      this._render();
      Promise.resolve(window.customElements.whenDefined('variant-picker')).then(() => {
        let attempts = 0;
        const connect = () => {
          if (!this._connected || generation !== this._pickerGeneration) return;
          const picker = this._findPicker();
          const state = this._pickerUsable(picker);
          if (!state) {
            attempts += 1;
            if (attempts < MAX_PICKER_ATTEMPTS) this._pickerTimer = setTimeout(connect, PICKER_RETRY_MS);
            else { this._pickerConnecting = false; this._render(); }
            return;
          }
          this._pickerAbort?.abort();
          this._pickerAbort = new AbortController();
          this._picker = picker;
          this._pickerForm = state.form;
          this._pickerConnecting = false;
          state.form.addEventListener('variant:change', () => this._render(), { signal: this._pickerAbort.signal });
          this._render();
        };
        connect();
      }).catch(() => {
        if (generation === this._pickerGeneration) { this._pickerConnecting = false; this._render(); }
      });
    }

    _activateInteractive() {
      if (this._controlsBound) return;
      this._controlsBound = true;
      this._fill(this._brand, this._text('select_brand'), [...new Set(['Samsung', 'Garmin', 'Huawei', 'Amazfit', 'Xiaomi', 'Ice Watch', 'Honor', ...(this._watches || []).map(w => w.brand)])].sort().map(brand => ({ value: brand, label: brand })));
      const unknown = document.createElement('option'); unknown.value = '__unknown'; unknown.textContent = this._text('unknown'); this._brand?.append(unknown);
      this._listenUi(this._brand, 'change', () => { this._searchEditing = false; this._fillModels(); this._saveWatch(); this._render(); });
      this._listenUi(this._model, 'change', () => { this._saveWatch(); this._render(); });
      this._listenUi(this._apply, 'click', () => this._applyRecommendation());
      this._bindSearch();
      const interactive = this.querySelector('[data-na-interactive]');
      const staticGuide = this.querySelector('[data-na-static]');
      if (interactive) interactive.hidden = false;
      if (staticGuide) staticGuide.hidden = true;
      this._renderAvailable();
    }

    _initialise() {
      if (!this._connected) return Promise.resolve(false);
      if (this._watches) {
        this._activateInteractive(); this._restoreWatch(true); this._connectPicker();
        return Promise.resolve(true);
      }
      if (this._initialising) return this._initialising;
      if ((this._registryAttempts || 0) >= MAX_REGISTRY_ATTEMPTS) return Promise.resolve(false);
      this._registryAttempts = (this._registryAttempts || 0) + 1;
      const generation = (this._registryGeneration || 0) + 1;
      this._registryGeneration = generation;
      const job = loadRegistry(this._registryUrl()).then(watches => {
        if (!this._connected || generation !== this._registryGeneration) return false;
        this._watches = watches;
        this._activateInteractive();
        this._restoreWatch();
        this._connectPicker();
        return true;
      }).catch(() => {
        // Le guide descriptif demeure disponible; une ouverture/pageshow pourra utiliser le budget restant.
        return false;
      }).finally(() => {
        if (this._initialising === job) this._initialising = null;
      });
      this._initialising = job;
      return job;
    }

    _bindSearch() {
      for (const button of this._brandButtons || []) this._listenUi(button, 'click', () => {
        const brand = button.dataset.naBrandChoice;
        if (!this._brand || !(brand === '__unknown' || this._watches.some(w => w.brand === brand))) return;
        if (this._brand.value === brand) return;
        this._searchEditing = false; this._brand.value = brand; this._fillModels(); this._saveWatch(); this._render();
      });
      this._listenUi(this._search, 'focus', () => this._showSuggestions());
      this._listenUi(this._search, 'input', () => {
        this._searchEditing = true;
        if (this._model) this._model.value = '';
        this._saveWatch(); this._render(); this._showSuggestions();
      });
      this._listenUi(this._search, 'keydown', event => {
        if (event.key === 'Escape') { this._hideSuggestions(); return; }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (this._suggestions?.hidden) this._showSuggestions();
          const length = this._choices?.length || 0; if (!length) return;
          this._activeIndex = (this._activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + length) % length;
          this._highlightChoice();
        } else if (event.key === 'Enter' && !this._suggestions?.hidden) {
          event.preventDefault(); const choice = this._choices?.[this._activeIndex]; if (choice) this._selectModel(choice.id);
        } else if (event.key === 'Tab') this._hideSuggestions();
      });
      this._listenUi(this._suggestions, 'click', event => {
        const button = event.target?.closest?.('[data-na-choice]');
        if (button && this._suggestions.contains(button)) this._selectModel(button.dataset.naChoice);
      });
      this._listenUi(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) this._hideSuggestions(); });
      this._listen(document, 'click', event => { if (!this.contains?.(event.target)) this._hideSuggestions(); });
      const find = this.querySelector('[data-na-find]'), help = this.querySelector('[data-na-find-help]');
      this._listenUi(find, 'click', () => { if (help) { help.hidden = !help.hidden; find.setAttribute('aria-expanded', String(!help.hidden)); } });
      this._listenUi(this.querySelector('[data-na-missing]'), 'click', () => this._selectModel('__unknown'));
    }
    _syncControls() {
      for (const button of this._brandButtons || []) button.setAttribute('aria-pressed', String(button.dataset.naBrandChoice === this._brand?.value));
      if (!this._search || this._searchEditing) return;
      const watch = (this._watches || []).find(w => w.id === this._model?.value && w.brand === this._brand?.value);
      this._search.value = watch?.name || '';
      this._hideSuggestions();
    }
    _hideSuggestions() {
      if (this._suggestions) this._suggestions.hidden = true;
      this._search?.setAttribute?.('aria-expanded', 'false');
      this._search?.removeAttribute?.('aria-activedescendant');
      this._activeIndex = -1;
    }
    _showSuggestions() {
      if (!this._search || !this._suggestions || !this._brand?.value || this._brand.value === '__unknown') return;
      const normalise = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const query = normalise(this._search.value), tokens = query.split(/\s+/).filter(Boolean);
      this._choices = (this._watches || []).filter(w => w.brand === this._brand.value && tokens.every(token => normalise(w.name).includes(token))).sort((a,b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
      this._suggestions.replaceChildren(); this._activeIndex = -1;
      for (const watch of this._choices) {
        const li = document.createElement('li'), button = document.createElement('button');
        li.setAttribute('role','presentation'); button.type = 'button'; button.dataset.naChoice = watch.id;
        button.id = this._search.id + '-choice-' + watch.id; button.setAttribute('role','option'); button.setAttribute('aria-selected','false'); button.tabIndex = -1;
        button.textContent = watch.name; li.append(button); this._suggestions.append(li);
      }
      if (!this._choices.length) { const li = document.createElement('li'); li.className = 'lbdb-na-guide__no-model'; li.textContent = this._text('manual_title'); this._suggestions.append(li); }
      this._suggestions.hidden = false; this._search.setAttribute('aria-expanded','true'); this._search.removeAttribute('aria-activedescendant');
    }
    _highlightChoice() {
      const buttons = Array.from(this._suggestions?.querySelectorAll('[data-na-choice]') || []);
      buttons.forEach((button,index) => button.setAttribute('aria-selected',String(index === this._activeIndex)));
      const active = buttons[this._activeIndex]; if (active) { this._search.setAttribute('aria-activedescendant',active.id); active.scrollIntoView?.({block:'nearest'}); }
    }
    _selectModel(modelId) {
      const watch = (this._watches || []).find(w => w.id === modelId && w.brand === this._brand?.value);
      if (!watch && modelId !== '__unknown') return;
      this._searchEditing = false; this._model.value = watch?.id || '__unknown'; this._saveWatch(); this._render();
      if (modelId === '__unknown') { const help = this.querySelector('[data-na-help]'); if (help) help.open = true; }
    }

    _fillModels() {
      const field = this.querySelector('[data-na-model-field]');
      if (field) field.hidden = !this._brand?.value || this._brand.value === '__unknown';
      this._fill(this._model, this._text('select_model'), (this._watches || []).filter(w => w.brand === this._brand?.value).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })).map(w => ({ value: w.id, label: w.name })));
      this._syncControls();
      if (this._brand?.value && this._brand.value !== '__unknown') { const option = document.createElement('option'); option.value = '__unknown'; option.textContent = this._text('unknown'); this._model?.append(option); }
    }
    _saveWatch() {
      const watch = (this._watches || []).find(w => w.id === this._model?.value && w.brand === this._brand?.value);
      this._syncControls();
      rememberWatch(watch || null); // Identifier la montre n’affirme pas qu’un bracelet lui convient.
      if (typeof document !== 'undefined') document.dispatchEvent?.(new CustomEvent(WATCH_EVENT, { detail: { source: this } }));
    }
    _restoreWatch(preserveDraft = false) {
      if (!this._brand || !this._model) return;
      const profile = recallWatch();
      // Une animation/reconnexion tardive ne doit pas effacer un choix partiel du client.
      // Les événements de mémoire externes gardent la restauration stricte par défaut.
      if (preserveDraft && !profile && this._brand.value && (!this._model.value || this._model.value === '__unknown')) { this._render(); return; }
      this._searchEditing = false;
      const watch = (this._watches || []).find(w => w.id === profile?.modelId && w.brand === profile?.brand);
      this._brand.value = watch?.brand || ''; this._fillModels(); this._model.value = watch?.id || ''; this._syncControls(); this._render();
    }
    _fill(select, placeholder, choices) {
      if (!select) return;
      select.replaceChildren();
      for (const item of [{ value: '', label: placeholder }, ...choices]) {
        const option = document.createElement('option'); option.value = item.value; option.textContent = item.label; select.append(option);
      }
    }
    _system(group) {
      const keys = { spring_bar_quick_release: 'quick_release', garmin_quickfit: 'quickfit', samsung_watch8: 'watch8', samsung_ultra: 'ultra' };
      return [this._text(keys[group.system]), group.width ? group.width + ' mm' : ''].filter(Boolean).join(' · ');
    }
    _renderAvailable() {
      const list = this.querySelector('[data-na-available]');
      if (!list) return;
      list.replaceChildren();
      for (const label of [...new Set(this._data.groups.map(group => this._system(group)))]) { const li = document.createElement('li'); li.textContent = label; list.append(li); }
    }

    _resolution() {
      if (this._pickerConnecting) return { status: 'loading' };
      const state = this._pickerUsable(this._picker, false);
      if (!state) return { status: 'incomplete' };
      const selected = this._brand?.value === '__unknown' ? '__unknown' : this._model?.value;
      // La résolution ne lit jamais les variantes Liquid d’un autre formulaire.
      return resolve({ ...this._data, variants: state.variants }, this._watches || [], selected, state.selected.options);
    }
    _render() {
      if (!this._result || !this._body || !this._value || !this._apply) return;
      const resolution = this._resolution();
      const status = resolution.status;
      this._result.hidden = status === 'incomplete';
      if (this._result.dataset) this._result.dataset.state = status === 'compatible' && this._pickerUsable(this._picker) ? 'ready' : 'verify';
      if (this._result.dataset) this._result.dataset.fixed = String(!!resolution.group && !resolution.group.width);
      if (this._title) this._title.textContent = this._text(status === 'compatible' ? 'result_title' : 'model_label');
      this._body.textContent = this._text(status === 'compatible' ? 'ready' : status).replace('{{ watch }}', resolution.watch?.name || '');
      this._value.textContent = resolution.group ? (resolution.value || this._system(resolution.group)) : '';
      if (this._connector) { this._connector.textContent = resolution.group ? this._system({ ...resolution.group, width: null }) : ''; this._connector.hidden = !resolution.group || !resolution.group.width; }
      this._value.hidden = !resolution.group;
      this._apply.hidden = status !== 'compatible' || !this._pickerUsable(this._picker);
      this._apply.disabled = this._apply.hidden;
      const help = this.querySelector('[data-na-help]');
      if (help && status === 'verify') help.open = true;
      else if (help && status === 'compatible') help.open = false;
      this._apply.textContent = this._text('apply');
      this._renderMatch();
    }
    _renderMatch() {
      const picker = this._picker;
      if (!picker?.querySelectorAll) return;
      this._clearPickerMatch(picker);
      const state = this._pickerUsable(picker, false);
      if (!state) return;
      const profile = recallWatch();
      const watch = (this._watches || []).find(w => w.id === profile?.modelId && w.brand === profile?.brand);
      const proof = watch && matchedWatch({ ...this._data, variants: state.variants }, this._watches || [], watch.id, state.selected);
      if (!proof || !this._text('watch_match')) return;
      const slots = safe(() => Array.from(picker.querySelectorAll('[data-na-watch-match]'))) || [];
      const slot = slots.find(node => Number(node.dataset.naWatchMatch) === proof.group.position);
      if (!slot) return;
      slot.textContent = this._text('watch_match').replace('{{ watch }}', proof.watch.name); slot.hidden = false;
      const input = (safe(() => Array.from(picker.querySelectorAll('input[name="option' + proof.group.position + '"]'))) || []).find(node => node.checked);
      const label = input && (safe(() => Array.from(picker.querySelectorAll('label[for]'))) || []).find(node => node.htmlFor === input.id);
      const badgeRect = slot.getBoundingClientRect?.(), labelRect = label?.getBoundingClientRect?.();
      if (badgeRect && labelRect) slot.style.setProperty('--na-watch-anchor', Math.max(14, Math.min(badgeRect.width - 14, labelRect.left + labelRect.width / 2 - badgeRect.left)) + 'px');
    }

    _applyRecommendation() {
      const picker = this._picker;
      const state = this._pickerUsable(picker, false);
      const failed = () => { if (this._body) this._body.textContent = this._text('selection_error'); };
      if (!state || !this._matchesGuideProduct(picker) || (this._associatedFormId() && state.formId !== this._associatedFormId())) { failed(); return; }
      const selected = this._brand?.value === '__unknown' ? '__unknown' : this._model?.value;
      // Relecture native au dernier moment : ID allowlist, options actuelles et stock doivent tous rester valides.
      const resolution = resolve({ ...this._data, variants: state.variants }, this._watches || [], selected, state.selected.options);
      if (resolution.status !== 'compatible' || typeof picker.selectVariant !== 'function') { failed(); return; }
      const target = state.variants.find(variant => id(variant.id) === id(resolution.variantId));
      const original = state.selected.options.slice();
      if (!target || target.available !== true || optionsOf(target).length !== this._data.options.length || optionsOf(target).some((value, index) => index !== resolution.group.position - 1 && value !== original[index])) { failed(); return; }
      this._body.textContent = this._text('changing');
      try { picker.selectVariant(Number(target.id)); } catch (_) { failed(); return; }
      const after = this._pickerState(picker);
      if (!after || id(after.master?.value) !== id(target.id) || id(after.selected?.id) !== id(target.id)) { failed(); return; }
      this._saveWatch(); this._renderMatch();
      this._body.textContent = this._text('applied');
      const drawer = this._drawer(); if (drawer && typeof drawer.hide === 'function') drawer.hide();
      picker.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { resolve, authorisedVariants, matchedWatch, LBDBNonAppleWatchGuide };
  if (typeof window !== 'undefined') window.LBDBNonAppleWatchSizing = Object.freeze({ resolve, authorisedVariants, matchedWatch });
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lbdb-nonapple-watch-guide')) window.customElements.define('lbdb-nonapple-watch-guide', LBDBNonAppleWatchGuide);
})();
